import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm, ValidationError } from "@formspree/react";
import { CheckCircle2, Check, Loader2, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TEMPLATES, BUSINESS_CATEGORIES } from "@/lib/templates";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID || "mzdyekno";

export default function GetStarted() {
  const [formspreeState, submitToFormspree] = useForm(FORMSPREE_ID);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    business_name: "",
    business_type: "",
    owner_name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const update = (key, val) => { setForm(p => ({ ...p, [key]: val })); setError(""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.business_type || !form.owner_name || !form.email) {
      setError("Please fill out the required fields.");
      return;
    }
    setLoading(true);
    try {
      // 1. Save to our database
      await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      // 2. Send email notification to Austin via Formspree
      const formData = new FormData();
      formData.append("_subject", `New SiteSmith Lead: ${form.business_name}`);
      formData.append("Business Name", form.business_name);
      formData.append("Business Type", form.business_type);
      formData.append("Owner Name", form.owner_name);
      formData.append("email", form.email);
      formData.append("Phone", form.phone || "Not provided");
      formData.append("Notes", form.notes || "None");

      // Use the Formspree React hook for proper submission
      await submitToFormspree(formData);

      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Show success screen
  if (submitted || formspreeState.succeeded) {
    return (
      <div className="min-h-screen bg-[#0A0F1F] flex items-center justify-center px-6" data-testid="get-started-success">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={36} className="text-emerald-500" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl font-extrabold text-white mb-3">We got it!</h1>
          <p className="text-slate-400 leading-relaxed mb-2">
            Austin or Jack will reach out to <span className="text-white font-medium">{form.email}</span> within 24 hours with a preview of your website and details on getting started.
          </p>
          <p className="text-slate-600 text-sm mb-8">Keep an eye on your inbox (and spam folder, just in case).</p>
          <Link to="/" className="inline-block bg-white/5 border border-white/10 text-white px-6 py-3 rounded-full font-medium hover:bg-white/10 transition-colors" data-testid="back-home-btn">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1F]" data-testid="get-started-page">
      {/* Header */}
      <div className="border-b border-white/[0.04] bg-[#0A0F1F]/95 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-lg font-extrabold text-white">Site<span className="text-slate-500 font-medium">Smith</span></Link>
          <a href="mailto:hello@sitesmith.com" className="text-sm text-slate-500 hover:text-slate-300 transition-colors flex items-center gap-1.5"><Mail size={14} /> Contact</a>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: The pitch */}
          <div className="lg:sticky lg:top-24">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-testid="get-started-headline">
              Tell us about your business. We'll handle the rest.
            </h1>
            <p className="text-slate-400 leading-relaxed mb-8">
              Fill out the form and Austin or Jack will personally reach out within 24 hours with a preview of your website and next steps.
            </p>
            <div className="space-y-4 mb-10">
              {["Fully custom website, built for your business", "$99/month. Hosting, security, and DDoS protection included", "No hidden fees. No contracts. Cancel anytime."].map((point, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0"><Check size={14} className="text-emerald-500" strokeWidth={2.5} /></div>
                  <span className="text-slate-300 text-sm">{point}</span>
                </div>
              ))}
            </div>
            <p className="text-slate-600 text-sm">Have a question? Fill out the form and we'll get back to you.</p>
          </div>

          {/* Right: The form */}
          <div className="bg-[#111827] border border-white/[0.06] rounded-lg p-6 sm:p-8" data-testid="get-started-form-container">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-900/20 border border-red-800/30 text-red-400 px-4 py-3 rounded-md text-sm" data-testid="form-error">{error}</div>
              )}

              {/* Show Formspree validation errors */}
              <ValidationError errors={formspreeState.errors} />

              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Business Name <span className="text-red-400">*</span></Label>
                <Input data-testid="form-business-name" value={form.business_name} onChange={e => update('business_name', e.target.value)} placeholder="e.g. Mike's Plumbing" className="bg-[#0A0F1F] border-white/10 text-white placeholder:text-slate-600 focus:ring-orange-600 focus:border-orange-600 rounded-md h-12" required />
              </div>

              <div>
                <Label className="text-slate-300 mb-2 block text-sm">What type of business? <span className="text-red-400">*</span></Label>
                <select data-testid="form-business-type" value={form.business_type} onChange={e => update('business_type', e.target.value)} className="w-full bg-[#0A0F1F] border border-white/10 text-white rounded-md h-12 px-3 text-sm focus:ring-orange-600 focus:border-orange-600 appearance-none" required>
                  <option value="" className="text-slate-600">Select your business type...</option>
                  {BUSINESS_CATEGORIES.map(cat => (
                    <optgroup key={cat} label={cat}>
                      {TEMPLATES.filter(t => t.category === cat).map(t => (<option key={t.id} value={t.name}>{t.name}</option>))}
                    </optgroup>
                  ))}
                  <option value="Other">Other (tell us below)</option>
                </select>
              </div>

              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Your Name <span className="text-red-400">*</span></Label>
                <Input data-testid="form-owner-name" value={form.owner_name} onChange={e => update('owner_name', e.target.value)} placeholder="e.g. Mike Johnson" className="bg-[#0A0F1F] border-white/10 text-white placeholder:text-slate-600 focus:ring-orange-600 focus:border-orange-600 rounded-md h-12" required />
              </div>

              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Email Address <span className="text-red-400">*</span></Label>
                <Input data-testid="form-email" type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="e.g. mike@email.com" className="bg-[#0A0F1F] border-white/10 text-white placeholder:text-slate-600 focus:ring-orange-600 focus:border-orange-600 rounded-md h-12" required />
                <ValidationError field="email" errors={formspreeState.errors} className="text-red-400 text-xs mt-1" />
              </div>

              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Phone Number</Label>
                <Input data-testid="form-phone" type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="e.g. (480) 555-1234" className="bg-[#0A0F1F] border-white/10 text-white placeholder:text-slate-600 focus:ring-orange-600 focus:border-orange-600 rounded-md h-12" />
                <p className="text-slate-600 text-xs mt-1.5">Optional, but helps us reach you faster</p>
              </div>

              <div>
                <Label className="text-slate-300 mb-2 block text-sm">Anything else we should know?</Label>
                <textarea data-testid="form-notes" value={form.notes} onChange={e => update('notes', e.target.value)} placeholder="Tell us about your services, your style preferences, or anything you'd like on your website." rows={3} className="w-full bg-[#0A0F1F] border border-white/10 text-white placeholder:text-slate-600 focus:ring-orange-600 focus:border-orange-600 rounded-md p-3 text-sm resize-none" />
              </div>

              <button type="submit" disabled={loading || formspreeState.submitting} data-testid="form-submit-btn"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3.5 rounded-full font-semibold text-base transition-colors btn-shimmer flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ boxShadow: '0 10px 30px -5px rgba(234,88,12,0.35)' }}
              >
                {(loading || formspreeState.submitting) && <Loader2 size={18} className="animate-spin" />}
                Submit Request
              </button>
              <p className="text-slate-600 text-xs text-center mt-3" data-testid="form-legal-acknowledgment">
                By submitting, you agree to our{" "}
                <Link to="/terms" className="text-slate-400 underline hover:text-slate-300">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy" className="text-slate-400 underline hover:text-slate-300">Privacy Policy</Link>.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
